<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { cardTemplates, generateCard, newCardElementId, type CardElement, type CardElementKind, type CardInput, type CardLive, type CardLook, type CardModel, type CardState } from '@vp/shared-types';
import { useApp } from '../stores/app';
import { locale, t } from '../i18n';
import { ask } from '../dialogs';
import { canvasOpens } from '../canvas-use';
import { cardEditor, removeUserCard, saveUserCard, userCard, userCards, UserCardLimitError } from '../user-cards';
import { requestShowPanel } from '../profile-panels';
import AppNav from '../components/AppNav.vue';
import Icon from '../components/Icon.vue';
import Toggle from '../components/Toggle.vue';
import LookEditor from '../components/LookEditor.vue';
import CardBackdrop from '../components/CardBackdrop.vue';
import ProfileCardFrame from '../components/ProfileCardFrame.vue';
import { frameCss } from '../card-look';

/**
 * The platform's card editor: the person makes a profile card of their own from shapes, text,
 * pictures, interactive elements and live data, with effects and a look. The card is drawn by the
 * very page and HTML the profile uses (`ProfileCardFrame`), so what is seen here is what the tray shows.
 * Elements: dragged and resized with the mouse, moved with the arrows (Shift — by 8), Delete removes;
 * Ctrl+Z / Ctrl+Shift+Z; pictures come from the clipboard (Ctrl+V).
 */
const app = useApp();

type Palette = { kind: CardElementKind | CardLive | 'profile'; icon: string; label: string };
const PALETTE: Palette[] = [
  { kind: 'rect', icon: 'rect', label: 'rect' },
  { kind: 'circle', icon: 'ellipse', label: 'circle' },
  { kind: 'line', icon: 'line', label: 'line' },
  { kind: 'text', icon: 'text', label: 'text' },
  { kind: 'icon', icon: 'reaction', label: 'icon' },
  { kind: 'image', icon: 'image', label: 'image' },
  { kind: 'button', icon: 'pill', label: 'button' },
  { kind: 'toggle', icon: 'sliders', label: 'toggle' },
  { kind: 'checkbox', icon: 'check', label: 'checkbox' },
  { kind: 'counter', icon: 'plus', label: 'counter' },
  { kind: 'note', icon: 'sticker', label: 'note' },
  { kind: 'time', icon: 'gauge', label: 'time' },
  { kind: 'date', icon: 'bookmark', label: 'date' },
  { kind: 'weekday', icon: 'list', label: 'weekday' },
  { kind: 'countdown', icon: 'refresh', label: 'countdown' },
  { kind: 'profile', icon: 'user', label: 'profile' },
];
const EFFECTS = ['none', 'appear', 'pulse', 'sway', 'spin', 'shimmer'] as const;
const TRIGGERS = ['always', 'hover', 'press', 'change'] as const;
const STATS = ['canvases', 'objects', 'opens', 'days'] as const;

const templates = computed(() => {
  void locale.value;
  return cardTemplates((k) => t('cardEditor.' + k));
});

function blank(): CardModel {
  return { title: '', w: 300, h: 200, look: { radius: 16 }, elements: [] };
}
const editingId = ref<string | null>(cardEditor.id);
const start = editingId.value ? userCard(editingId.value)?.model : undefined;
const model = reactive<CardModel>(start ? JSON.parse(JSON.stringify(start)) : JSON.parse(JSON.stringify(templates.value[0]?.model ?? blank())));
const sel = ref<string | null>(null);
const preview = ref(false);
const grid = ref(true);
const dirty = ref(false);
const undo: string[] = [];
const redo: string[] = [];
const previewState = reactive<CardState>({});
const message = ref('');

const generated = computed(() => generateCard(model));
const selected = computed(() => model.elements.find((e) => e.id === sel.value) ?? null);
const stats = computed(() => ({ canvases: app.canvases.length, objects: app.canvases.reduce((s, c) => s + c.objectCount, 0), opens: Object.values(canvasOpens()).reduce((a, b) => a + b, 0), days: 1 }));

function snapshot(): string {
  return JSON.stringify(model);
}
function restore(json: string): void {
  Object.assign(model, JSON.parse(json) as CardModel);
}
function commit(): void {
  undo.push(snapshot());
  if (undo.length > 100) undo.shift();
  redo.length = 0;
  dirty.value = true;
}
function change(fn: () => void): void {
  commit();
  fn();
}
function doUndo(): void {
  if (!undo.length) return;
  redo.push(snapshot());
  restore(undo.pop()!);
  sel.value = null;
}
function doRedo(): void {
  if (!redo.length) return;
  undo.push(snapshot());
  restore(redo.pop()!);
  sel.value = null;
}
/** A field edit is one undo step however many keys it takes. */
let lastField = '';
function edit(field: string, fn: () => void): void {
  const k = (sel.value ?? 'card') + ':' + field;
  if (k !== lastField) {
    commit();
    lastField = k;
  }
  fn();
}
watch(sel, () => (lastField = ''));

const snap = (v: number) => (grid.value ? Math.round(v / 8) * 8 : Math.round(v));

function add(p: Palette): void {
  if (p.kind === 'image') {
    message.value = t('cardEditor.pasteHint');
    return;
  }
  const e: CardElement = { id: newCardElementId(), kind: 'rect', x: snap(model.w / 2 - 60), y: snap(model.h / 2 - 20), w: 120, h: 40, color: '#1f2937', fontSize: 15 };
  const key = 'k' + Date.now().toString(36).slice(-5);
  switch (p.kind) {
    case 'rect':
    case 'circle':
      Object.assign(e, { kind: p.kind, w: 64, h: 64, fill: '#93c5fd' });
      break;
    case 'line':
      Object.assign(e, { kind: 'line', w: 160, h: 4, color: '#94a3b8' });
      break;
    case 'text':
      Object.assign(e, { kind: 'text', text: t('cardEditor.el.text') });
      break;
    case 'icon':
      Object.assign(e, { kind: 'icon', text: '⭐', w: 56, h: 56, fontSize: 36 });
      break;
    case 'button':
      Object.assign(e, { kind: 'button', text: t('cardEditor.el.button'), fill: '#5e9cff', color: '#ffffff' });
      break;
    case 'toggle':
      Object.assign(e, { kind: 'toggle', w: 56, h: 30, fill: '#22c55e', key });
      break;
    case 'checkbox':
      Object.assign(e, { kind: 'checkbox', text: t('cardEditor.el.checkbox'), w: 180, h: 28, key });
      break;
    case 'counter':
      Object.assign(e, { kind: 'counter', w: 150, h: 44, fontSize: 22, min: 0, max: 99, step: 1, key });
      break;
    case 'note':
      Object.assign(e, { kind: 'note', w: 220, h: 90, text: t('cardEditor.el.note'), key });
      break;
    default:
      Object.assign(e, { kind: 'live', live: p.kind === 'profile' ? 'profile:canvases' : p.kind, w: 180, h: 40, fontSize: 24, bold: true, ...(p.kind === 'countdown' ? { date: `${new Date().getFullYear() + 1}-01-01T00:00:00` } : {}) });
  }
  change(() => model.elements.push(e));
  sel.value = e.id;
}

function removeSelected(): void {
  const e = selected.value;
  if (e) change(() => model.elements.splice(model.elements.indexOf(e), 1));
  sel.value = null;
}

function useTemplate(m: CardModel): void {
  change(() => restore(JSON.stringify(m)));
  sel.value = null;
}

// ---- the stage: the card at its design size, scaled to the room there is
const wrap = ref<HTMLElement | null>(null);
const room = ref({ w: 800, h: 600 });
const scale = computed(() => Math.max(0.3, Math.min(2, (room.value.w - 48) / model.w, (room.value.h - 48) / model.h)));
let observer: ResizeObserver | null = null;
const stageEl = ref<HTMLElement | null>(null);

let drag: { e: CardElement; mode: 'move' | 'resize'; x: number; y: number; o: { x: number; y: number; w: number; h: number }; moved: boolean } | null = null;
function grab(ev: PointerEvent, e: CardElement): void {
  if (preview.value || ev.button !== 0) return;
  ev.preventDefault();
  stageEl.value?.focus({ preventScroll: true });
  sel.value = e.id;
  drag = { e, mode: (ev.target as HTMLElement).classList.contains('rs') ? 'resize' : 'move', x: ev.clientX, y: ev.clientY, o: { x: e.x, y: e.y, w: e.w, h: e.h }, moved: false };
  (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
}
function move(ev: PointerEvent): void {
  if (!drag) return;
  const dx = (ev.clientX - drag.x) / scale.value;
  const dy = (ev.clientY - drag.y) / scale.value;
  if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 2) return;
  if (!drag.moved) {
    commit();
    drag.moved = true;
  }
  const e = drag.e;
  if (drag.mode === 'resize') {
    e.w = Math.max(8, snap(drag.o.w + dx));
    e.h = Math.max(4, snap(drag.o.h + dy));
  } else {
    e.x = Math.min(model.w - 8, Math.max(8 - e.w, snap(drag.o.x + dx)));
    e.y = Math.min(model.h - 8, Math.max(8 - e.h, snap(drag.o.y + dy)));
  }
}
function drop(): void {
  drag = null;
}

function onKey(ev: KeyboardEvent): void {
  const target = ev.target as HTMLElement | null;
  if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
  const mod = ev.ctrlKey || ev.metaKey;
  if (mod && ev.code === 'KeyZ') {
    ev.preventDefault();
    if (ev.shiftKey) doRedo();
    else doUndo();
    return;
  }
  if (mod && ev.code === 'KeyY') {
    ev.preventDefault();
    doRedo();
    return;
  }
  const e = selected.value;
  if (!e || preview.value) return;
  const d = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[ev.key];
  if (d) {
    ev.preventDefault();
    const s = ev.shiftKey ? 8 : 1;
    edit('nudge', () => {
      e.x += d[0] * s;
      e.y += d[1] * s;
    });
  } else if (ev.key === 'Delete' || ev.key === 'Backspace') {
    ev.preventDefault();
    removeSelected();
  } else if (ev.key === 'Escape') sel.value = null;
}

/** Pictures come from the clipboard only, up to 500 KB. */
function onPaste(ev: ClipboardEvent): void {
  const f = [...(ev.clipboardData?.files ?? [])].find((x) => /^image\/(png|jpeg|webp|gif)$/.test(x.type));
  if (!f) return;
  ev.preventDefault();
  if (f.size > 500_000) {
    message.value = t('cardEditor.tooBig');
    return;
  }
  const r = new FileReader();
  r.onload = () => {
    const e: CardElement = { id: newCardElementId(), kind: 'image', x: 16, y: 16, w: 120, h: 90, src: String(r.result) };
    change(() => model.elements.push(e));
    sel.value = e.id;
  };
  r.readAsDataURL(f);
}

function onInput(input: CardInput): void {
  if ('key' in input) previewState[input.key] = input.value;
}
watch(preview, (on) => {
  sel.value = null;
  if (!on) for (const k of Object.keys(previewState)) delete previewState[k];
});

// ---- saving
function save(): void {
  if (!model.title.trim()) model.title = t('cardEditor.untitled');
  try {
    const fresh = !editingId.value;
    const id = saveUserCard(editingId.value, model);
    editingId.value = id;
    cardEditor.id = id;
    if (fresh) requestShowPanel(`user:${id}`);
    dirty.value = false;
    message.value = '';
    app.notify('info', fresh ? t('cardEditor.added') : t('cardEditor.updated'), 2500);
  } catch (e) {
    message.value = e instanceof UserCardLimitError ? t('cardEditor.limit') : String(e);
  }
}
async function back(): Promise<void> {
  if (dirty.value && !(await ask(t('cardEditor.leaveAsk'), { okLabel: t('cardEditor.leave') }))) return;
  app.go('profile');
}
async function remove(): Promise<void> {
  if (!editingId.value || !(await ask(t('cardEditor.removeAsk'), { danger: true, okLabel: t('cardEditor.remove') }))) return;
  removeUserCard(editingId.value);
  dirty.value = false;
  app.go('profile');
}
function openCard(id: string): void {
  const c = userCard(id);
  if (!c) return;
  commit();
  restore(JSON.stringify(c.model));
  editingId.value = id;
  cardEditor.id = id;
  sel.value = null;
  dirty.value = false;
}
function newCard(): void {
  commit();
  restore(JSON.stringify(blank()));
  editingId.value = null;
  cardEditor.id = null;
  sel.value = null;
}

function setLook(look: CardLook): void {
  edit('look', () => (model.look = look));
}
const v = (ev: Event) => (ev.target as HTMLInputElement).value;
const n = (ev: Event) => Number((ev.target as HTMLInputElement).value);

onMounted(() => {
  window.addEventListener('keydown', onKey);
  window.addEventListener('paste', onPaste);
  observer = new ResizeObserver(() => {
    if (wrap.value) room.value = { w: wrap.value.clientWidth, h: wrap.value.clientHeight };
  });
  if (wrap.value) observer.observe(wrap.value);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('paste', onPaste);
  observer?.disconnect();
});
</script>

<template>
  <div class="card-editor">
    <AppNav />
    <main class="page">
      <div class="bar float pillbar">
        <button type="button" class="pill" data-act="card-back" @click="back"><Icon name="chevronLeft" :size="15" />{{ t('cardEditor.back') }}</button>
        <input v-model="model.title" class="input title" type="text" maxlength="80" :placeholder="t('cardEditor.titlePlaceholder')" :aria-label="t('cardEditor.titlePlaceholder')" @input="dirty = true" />
        <button type="button" class="icon-btn" :aria-label="t('cardEditor.undo')" :title="t('cardEditor.undo')" @click="doUndo"><Icon name="undo" :size="16" /></button>
        <button type="button" class="icon-btn" :aria-label="t('cardEditor.redo')" :title="t('cardEditor.redo')" @click="doRedo"><Icon name="redo" :size="16" /></button>
        <button type="button" class="icon-btn" :class="{ active: grid }" :aria-pressed="grid" :aria-label="t('cardEditor.grid')" :title="t('cardEditor.grid')" @click="grid = !grid"><Icon name="grid" :size="16" /></button>
        <button type="button" class="pill" :class="{ active: preview }" :aria-pressed="preview" data-act="card-preview" @click="preview = !preview"><Icon name="monitor" :size="15" />{{ t('cardEditor.preview') }}</button>
        <span class="spacer"></span>
        <button v-if="editingId" type="button" class="icon-btn danger" :aria-label="t('cardEditor.remove')" :title="t('cardEditor.remove')" data-act="card-remove" @click="remove"><Icon name="trash" :size="16" /></button>
        <button type="button" class="pill primary" data-act="card-save" @click="save"><Icon name="check" :size="15" />{{ editingId ? t('cardEditor.save') : t('cardEditor.add') }}</button>
      </div>
      <p v-if="message" class="message" role="status">{{ message }}</p>

      <div class="layout">
        <aside class="side group">
          <p class="sec">{{ t('cardEditor.elements') }}</p>
          <div class="palette">
            <button v-for="p in PALETTE" :key="p.kind" type="button" class="tile" :data-add="p.kind" :title="t('cardEditor.el.' + p.label)" @click="add(p)">
              <Icon :name="p.icon" :size="18" />
              <span>{{ t('cardEditor.el.' + p.label) }}</span>
            </button>
          </div>
          <p class="sec">{{ t('cardEditor.templates') }}</p>
          <div class="list">
            <button v-for="tpl in templates" :key="tpl.id" type="button" class="pill item" :data-tpl="tpl.id" @click="useTemplate(tpl.model)">{{ tpl.name }}</button>
          </div>
          <p class="sec">{{ t('cardEditor.mine') }}</p>
          <div class="list">
            <button type="button" class="pill item" data-act="card-new" @click="newCard"><Icon name="plus" :size="14" />{{ t('cardEditor.new') }}</button>
            <button v-for="[id, c] in userCards()" :key="id" type="button" class="pill item" :class="{ active: id === editingId }" :data-card="id" @click="openCard(id)">{{ c.model.title || t('cardEditor.untitled') }}</button>
          </div>
        </aside>

        <section ref="wrap" class="stage-wrap" :class="{ grid }" @pointerdown.self="sel = null">
          <div class="stage-box" :style="{ width: model.w * scale + 'px', height: model.h * scale + 'px' }">
            <div ref="stageEl" class="stage" tabindex="-1" :style="{ width: model.w + 'px', height: model.h + 'px', transform: `scale(${scale})`, ...frameCss(model.look) }" data-card-stage @pointerdown.self="sel = null">
              <CardBackdrop :look="model.look" base="var(--panel)" />
              <ProfileCardFrame class="frame" :html="generated.html" :uses="generated.uses" :title="model.title" :stats="stats" :editing="!preview" :local-state="previewState" @input="onInput" />
              <div v-if="!preview" class="hits">
                <div
                  v-for="e in model.elements"
                  :key="e.id"
                  class="hit"
                  :class="{ sel: e.id === sel }"
                  :data-el="e.id"
                  :style="{ left: e.x + 'px', top: e.y + 'px', width: e.w + 'px', height: e.h + 'px' }"
                  @pointerdown="grab($event, e)"
                  @pointermove="move"
                  @pointerup="drop"
                  @pointercancel="drop"
                >
                  <span class="rs"></span>
                </div>
              </div>
            </div>
          </div>
          <p class="hint">{{ preview ? t('cardEditor.previewHint') : t('cardEditor.keysHint') }}</p>
        </section>

        <aside class="props group">
          <template v-if="selected">
            <header class="head">
              <strong>{{ t('cardEditor.el.' + (selected.kind === 'live' ? (selected.live?.startsWith('profile:') ? 'profile' : selected.live) : selected.kind)) }}</strong>
              <button type="button" class="icon-btn" :aria-label="t('cardEditor.deleteEl')" :title="t('cardEditor.deleteEl')" data-act="delete-el" @click="removeSelected"><Icon name="trash" :size="15" /></button>
            </header>
            <div v-if="['text', 'icon', 'button', 'checkbox', 'note'].includes(selected.kind)" class="row">
              <span class="lbl">{{ t('cardEditor.p.text') }}</span>
              <textarea v-if="selected.kind === 'text'" class="input grow" rows="2" :value="selected.text" @input="edit('text', () => (selected!.text = v($event)))"></textarea>
              <input v-else class="input grow" type="text" :value="selected.text" @input="edit('text', () => (selected!.text = v($event)))" />
            </div>
            <div class="row">
              <span class="lbl">{{ t('cardEditor.p.colors') }}</span>
              <label v-if="!['image', 'rect', 'circle'].includes(selected.kind)" class="swatch-btn" :title="t('cardEditor.p.color')" :style="{ background: selected.color ?? '#1f2937' }"><input type="color" :value="selected.color ?? '#1f2937'" @input="edit('color', () => (selected!.color = v($event)))" /></label>
              <label v-if="['rect', 'circle', 'button', 'toggle'].includes(selected.kind)" class="swatch-btn" :title="t('cardEditor.p.fill')" :style="{ background: selected.fill ?? '#93c5fd' }"><input type="color" :value="selected.fill ?? '#93c5fd'" @input="edit('fill', () => (selected!.fill = v($event)))" /></label>
            </div>
            <template v-if="['text', 'icon', 'button', 'checkbox', 'counter', 'note', 'live'].includes(selected.kind)">
              <div class="row">
                <span class="lbl">{{ t('cardEditor.p.size') }}</span>
                <input class="num-pill" type="number" min="6" max="200" :value="selected.fontSize ?? 14" @input="edit('size', () => (selected!.fontSize = Math.max(6, Math.min(200, n($event)))))" />
                <Toggle :model-value="!!selected.bold" :label="t('cardEditor.p.bold')" @update:model-value="(b) => edit('bold', () => (selected!.bold = b))" />
                <span class="mini">{{ t('cardEditor.p.bold') }}</span>
              </div>
              <div class="row">
                <span class="lbl">{{ t('cardEditor.p.align') }}</span>
                <div class="seg-pills" role="radiogroup">
                  <button v-for="a in ['left', 'center', 'right'] as const" :key="a" type="button" role="radio" :aria-checked="(selected.align ?? 'left') === a" :class="{ active: (selected.align ?? 'left') === a }" :aria-label="t('cardEditor.align.' + a)" @click="edit('align', () => (selected!.align = a))">
                    <Icon :name="a === 'left' ? 'textLeft' : a === 'center' ? 'textCenter' : 'textRight'" :size="14" />
                  </button>
                </div>
              </div>
            </template>
            <div v-if="selected.kind === 'button'" class="row">
              <span class="lbl">{{ t('cardEditor.p.link') }}</span>
              <input class="input grow" type="text" placeholder="https://… · canvas:…" :value="selected.link ?? ''" @input="edit('link', () => (selected!.link = v($event).trim()))" />
            </div>
            <div v-if="selected.kind === 'counter'" class="row">
              <span class="lbl">{{ t('cardEditor.p.range') }}</span>
              <input class="num-pill" type="number" :title="t('cardEditor.p.min')" :value="selected.min ?? 0" @input="edit('min', () => (selected!.min = n($event)))" />
              <input class="num-pill" type="number" :title="t('cardEditor.p.max')" :value="selected.max ?? 99" @input="edit('max', () => (selected!.max = n($event)))" />
              <input class="num-pill" type="number" :title="t('cardEditor.p.step')" :value="selected.step ?? 1" @input="edit('step', () => (selected!.step = n($event)))" />
            </div>
            <div v-if="selected.kind === 'live' && selected.live === 'countdown'" class="row">
              <span class="lbl">{{ t('cardEditor.p.date') }}</span>
              <input class="input grow" type="datetime-local" :value="(selected.date ?? '').slice(0, 16)" @input="edit('date', () => (selected!.date = v($event) + ':00'))" />
            </div>
            <div v-if="selected.kind === 'live' && selected.live?.startsWith('profile:')" class="row">
              <span class="lbl">{{ t('cardEditor.p.stat') }}</span>
              <select class="input grow" :value="selected.live" @change="edit('stat', () => (selected!.live = v($event) as CardLive))">
                <option v-for="s in STATS" :key="s" :value="'profile:' + s">{{ t('cardEditor.stat.' + s) }}</option>
              </select>
            </div>
            <label v-if="['rect', 'image'].includes(selected.kind)" class="row">
              <span class="lbl">{{ t('cardEditor.p.radius') }}</span>
              <input class="range" type="range" min="0" max="60" :value="selected.radius ?? 0" @input="edit('radius', () => (selected!.radius = n($event)))" />
            </label>
            <label class="row">
              <span class="lbl">{{ t('cardEditor.p.opacity') }}</span>
              <input class="range" type="range" min="0.1" max="1" step="0.05" :value="selected.opacity ?? 1" @input="edit('opacity', () => (selected!.opacity = n($event)))" />
            </label>
            <label class="row">
              <span class="lbl">{{ t('cardEditor.p.rotate') }}</span>
              <input class="range" type="range" min="-180" max="180" :value="selected.rot ?? 0" @input="edit('rot', () => (selected!.rot = n($event)))" />
            </label>
            <p class="sec">{{ t('cardEditor.p.effect') }}</p>
            <div class="chips" role="radiogroup" :aria-label="t('cardEditor.p.effect')">
              <button v-for="f in EFFECTS" :key="f" type="button" role="radio" class="pill small" :aria-checked="(selected.effect ?? 'none') === f" :class="{ active: (selected.effect ?? 'none') === f }" :data-fx="f" @click="edit('fx', () => (selected!.effect = f))">{{ t('cardEditor.fx.' + f) }}</button>
            </div>
            <template v-if="selected.effect && selected.effect !== 'none'">
              <p class="sec">{{ t('cardEditor.p.trigger') }}</p>
              <div class="chips" role="radiogroup" :aria-label="t('cardEditor.p.trigger')">
                <button v-for="tr in TRIGGERS" :key="tr" type="button" role="radio" class="pill small" :aria-checked="(selected.trigger ?? 'always') === tr" :class="{ active: (selected.trigger ?? 'always') === tr }" :data-tr="tr" @click="edit('tr', () => (selected!.trigger = tr))">{{ t('cardEditor.tr.' + tr) }}</button>
              </div>
            </template>
          </template>
          <template v-else>
            <header class="head"><strong>{{ t('cardEditor.card') }}</strong></header>
            <div class="row">
              <span class="lbl">{{ t('cardEditor.p.cardSize') }}</span>
              <input class="num-pill" type="number" min="120" max="760" :value="model.w" @change="edit('w', () => (model.w = Math.min(760, Math.max(120, n($event)))))" />
              <span class="mini">×</span>
              <input class="num-pill" type="number" min="84" max="483" :value="model.h" @change="edit('h', () => (model.h = Math.min(483, Math.max(84, n($event)))))" />
            </div>
            <LookEditor inline :look="model.look" :title="model.title" @change="setLook" @reset="setLook({ radius: 16 })" />
          </template>
        </aside>
      </div>
    </main>
  </div>
</template>

<style scoped>
.card-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg);
}
.page {
  flex: 1;
  min-height: 0;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px clamp(16px, 2.6vw, 44px) 16px;
}
.bar {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px;
  min-width: 0;
}
.title {
  flex: 0 1 300px;
  min-width: 100px;
}
.spacer {
  flex: 1;
}
.icon-btn.danger {
  color: var(--danger);
}
.message {
  margin: 0;
  padding: 0 6px;
  font-size: 12.5px;
  color: var(--text-2);
}
.layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 268px minmax(0, 1fr) 312px;
  gap: 14px;
}
.group {
  background: var(--panel);
  border-radius: var(--radius-panel);
  box-shadow: var(--shadow);
  padding: 12px 14px;
  overflow: auto;
}
.sec {
  margin: 10px 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-3);
}
.sec:first-child {
  margin-top: 0;
}
.palette {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}
.tile {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 7px 9px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 12.5px;
  cursor: pointer;
  text-align: left;
}
.tile span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tile:hover {
  border-color: color-mix(in srgb, var(--accent) 55%, var(--border));
  background: color-mix(in srgb, var(--accent) 6%, transparent);
}
.list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.list .item {
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stage-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 0;
  border-radius: var(--radius-panel);
  background: color-mix(in srgb, var(--text) 5%, var(--bg));
  overflow: hidden;
}
.stage-wrap.grid {
  background-image: radial-gradient(color-mix(in srgb, var(--text) 18%, transparent) 1px, transparent 1px);
  background-size: 16px 16px;
}
.stage-box {
  position: relative;
  flex: none;
}
.stage {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  border-radius: 16px;
  background: var(--panel);
  box-shadow: var(--shadow-soft);
  outline: none;
}
.frame {
  position: absolute;
  inset: 0;
  height: 100%;
  z-index: 1;
}
.hits {
  position: absolute;
  inset: 0;
  z-index: 2;
}
.hit {
  position: absolute;
  border: 1px dashed transparent;
  border-radius: 4px;
  cursor: move;
  touch-action: none;
}
.hit:hover {
  border-color: color-mix(in srgb, var(--accent) 55%, transparent);
}
.hit.sel {
  border: 1.5px solid var(--accent);
}
.rs {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--accent);
  cursor: nwse-resize;
  display: none;
}
.hit.sel .rs {
  display: block;
}
.hint {
  margin: 0;
  font-size: 12px;
  color: var(--text-3);
}
.props {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13.5px;
}
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}
.lbl {
  flex: 0 0 96px;
  font-size: 13px;
  color: var(--text-2);
}
.grow {
  flex: 1;
  min-width: 0;
}
.mini {
  font-size: 12px;
  color: var(--text-3);
}
.range {
  flex: 1;
  min-width: 80px;
  accent-color: var(--accent);
}
.swatch-btn {
  position: relative;
  overflow: hidden;
}
.swatch-btn input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chips .pill.small {
  font-size: 12.5px;
}
.props .seg-pills {
  align-self: flex-start;
}
@media (max-width: 1000px) {
  .layout {
    grid-template-columns: 1fr;
    overflow: auto;
  }
  .stage-wrap {
    min-height: 360px;
  }
}
</style>
